import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatTime } from "../../utilities/utilities";

const DocumentIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-5 w-5 text-[#46BDE1]"
    aria-hidden="true"
  >
    <path
      d="M7 3.75h6.75L18.25 8.25V20.25H7V3.75Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M13.75 3.75V8.25H18.25"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 12.25h5M9.5 15.5h5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const btnBase =
  "inline-flex h-9 items-center justify-center rounded-lg px-3.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50";

function TemplateRow({
  createdBy,
  createdAt,
  name,
  title,
  category,
  editButton = false,
  viewButton = true,
  useButton = false,
  onUseClick,
  useLoading = false,
  viewLoading = false,
  compact = false,
  onDelete,
  onViewClick,
  id,
  module,
}) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const editHandler = () => {
    if (module === "clause") {
      navigate(`/app/templates/clause/${id}`);
      return;
    }
    navigate(`/app/templates/edit/template/${id}`);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleteLoading(true);
    try {
      await onDelete(id);
    } finally {
      setDeleteLoading(false);
      setConfirmDelete(false);
    }
  };

  const displayName = name || title;

  return (
    <div
      className={`group flex items-center justify-between gap-4 border border-slate-200 bg-white transition hover:border-[#46BDE1]/50 hover:bg-slate-50/60 ${
        compact
          ? "mb-2 rounded-lg px-3.5 py-3"
          : "mb-3 rounded-xl px-4 py-4"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={`flex shrink-0 items-center justify-center rounded-lg bg-[#46BDE1]/10 ${
            compact ? "h-9 w-9" : "h-11 w-11"
          }`}
        >
          <DocumentIcon />
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <h3
              className="truncate text-[15px] font-semibold text-slate-900"
              title={displayName}
            >
              {displayName}
            </h3>
            {category && (
              <span className="hidden shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-600 sm:inline-flex">
                {category}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {createdBy?.name && <span>Created by {createdBy.name}</span>}
            {createdAt && (
              <span>
                {formatDate(createdAt)} · {formatTime(createdAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {viewButton && (
          <button
            type="button"
            onClick={onViewClick}
            disabled={viewLoading}
            className={`${btnBase} border border-slate-200 bg-white text-slate-700 hover:border-[#46BDE1] hover:text-[#1a8fb3]`}
          >
            {viewLoading ? "Loading..." : "Preview"}
          </button>
        )}

        {useButton && (
          <button
            type="button"
            onClick={onUseClick}
            disabled={useLoading}
            className={`${btnBase} bg-[#46BDE1] text-white hover:bg-[#3aa8ca]`}
          >
            {useLoading ? "Applying..." : "Use"}
          </button>
        )}

        {editButton && (
          <button
            type="button"
            onClick={editHandler}
            className={`${btnBase} border border-slate-200 bg-white text-slate-700 hover:border-[#46BDE1] hover:text-[#1a8fb3]`}
          >
            Edit
          </button>
        )}

        {editButton && !confirmDelete && (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className={`${btnBase} border border-red-100 bg-white text-red-600 hover:border-red-300 hover:bg-red-50`}
          >
            Delete
          </button>
        )}

        {editButton && confirmDelete && (
          <>
            <button
              type="button"
              disabled={deleteLoading}
              onClick={handleDelete}
              className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}
            >
              {deleteLoading ? "Deleting..." : "Confirm"}
            </button>
            <button
              type="button"
              disabled={deleteLoading}
              onClick={() => setConfirmDelete(false)}
              className={`${btnBase} border border-slate-200 bg-white text-slate-600 hover:bg-slate-50`}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default TemplateRow;
